import * as React from 'react';
import Container from '@mui/material/Container';
import Typography from '@mui/material/Typography';
import Box from '@mui/material/Box';
import Card from '@mui/material/Card';
import CardContent from '@mui/material/CardContent';
import CardActions from '@mui/material/CardActions';
import Button from '@mui/material/Button';
import TextField from '@mui/material/TextField';
import Backdrop from '@mui/material/Backdrop';
import CircularProgress from '@mui/material/CircularProgress';
import Stack from '@mui/material/Stack';
import apiClient, { shouldRedirectToLogin, getErrorMessage } from '../services/api-client';
import { alpha } from '@mui/material/styles';

import InputAdornment from '@mui/material/InputAdornment';
import IconButton from '@mui/material/IconButton';
import SearchIcon from '@mui/icons-material/Search';

import Dialog from '@mui/material/Dialog';
import DialogTitle from '@mui/material/DialogTitle';
import DialogContent from '@mui/material/DialogContent';
import DialogContentText from '@mui/material/DialogContentText';
import DialogActions from '@mui/material/DialogActions';
import PublishedWithChangesIcon from '@mui/icons-material/PublishedWithChanges';

import dayjs from 'dayjs';

import Snackbar from '@mui/material/Snackbar';
import Alert from '@mui/material/Alert';

import { Navigate, useNavigate } from 'react-router-dom';

import PageHeader from '../components/PageHeader';

export default function Users({setNavbarState, navbarState, window}) {
  const [approvals, setApprovals] = React.useState([]);
  const [searchValue, setSearchValue] = React.useState('');
  const [loading, setLoading] = React.useState(true);
  const [redirect, setRedirect] = React.useState({ trigger: false, route: '/' });
  const navigate = useNavigate();
  const [snackbar, setSnackbar] = React.useState({ open: false, message: '', severity: 'success' });
  const [approvalCache,setApprovalCache] = React.useState({})
  const [confirmDialog, setConfirmDialog] = React.useState({ open: false, approval: null });

  const unitType = localStorage.getItem('unit_type');

  const closeConfirmDialog = () => {
    setConfirmDialog({ open: false, approval: null });
  }

  // filter the approvals based on the search value
  const filteredApprovals = approvals.filter(approval =>
    approval.nombre.toLowerCase().includes(searchValue.toLowerCase())
    || approval.usuario.toLowerCase().includes(searchValue.toLowerCase())
    || approval.unidad.toLowerCase().includes(searchValue.toLowerCase())
    || approval.nombre_amenidad.toLowerCase().includes(searchValue.toLowerCase())
  );

  const handleSnackbarClose = (event, reason) => {
    if (reason === 'clickaway') {
      return;
    }
    setSnackbar({ ...snackbar, open: false });
  };

  function convertEpochsToTimeString(epoch1, epoch2) {
    const date1 = new Date(epoch1 * 1000);
    const date2 = new Date(epoch2 * 1000);

    const day = date1.getDate();
    const month = date1.toLocaleString('default', { month: 'short' });
    const time1 = date1.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });
    const time2 = date2.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });

    return `${day}/${month} ${time1} - ${time2}`;
  }

  const approvePre = (id) => {
    let approvalObj = approvalCache[id]
    approvalObj["id"] = id
    approvalObj['action'] = 'aprobar'
    approvalObj['title'] = 'Confirmar Aprobacion'
    setConfirmDialog({ open: true, approval: approvalObj });
  }

  const removePre = (id) => {
    let approvalObj = approvalCache[id]
    approvalObj["id"] = id
    approvalObj['action'] = 'eliminar'
    approvalObj['title'] = 'Confirmar Eliminacion'
    setConfirmDialog({ open: true, approval: approvalObj });
  }

  const runConfirmedAction = (id,action) => {  
    if (action === 'aprobar') {
      approve(id)
    } else if (action === 'eliminar') {
      remove(id)
    }
  }

  const approve = async (id) => {
    const data = { reservacion: id };
    closeConfirmDialog();
    setLoading(true);
    try {
      const response = await apiClient.post('approvals/approve', data, {}, { action: 'approve_reservation' });
      if (response.status === 200) {
        setSnackbar({ open: true, message: "Se ha aprobado la reservacion exitosamente.", severity: 'success' });
        fetchPendingApprovals();
      }
    } catch (error) {
      setSnackbar({ open: true, message: getErrorMessage(error, "Hubo un error al aprobar la reservacion, intenta de nuevo mas tarde."), severity: 'error' });
      console.error(error);
    } finally {
      setLoading(false);
    }
  }

  const convertEpochToDateString = (timestampString) => {
    const timestamp = timestampString.split('-')[1];
    const date = dayjs.unix(timestamp);
    return date.format('DDMMYY');
  };

  const remove = async (id) => {
    const data = { reservacion: id, dia: convertEpochToDateString(id) };
    closeConfirmDialog();
    setLoading(true);
    try {
      const response = await apiClient.post('reservation/delete', data, {}, { action: 'delete_reservation' });
      if (response.status === 200) {
        setSnackbar({ open: true, message: "Se ha eliminado la reservacion exitosamente.", severity: 'success' });
        fetchPendingApprovals();
      }
    } catch (error) {
      setSnackbar({ open: true, message: getErrorMessage(error, "Hubo un error al eliminar la reservacion, intenta de nuevo mas tarde."), severity: 'error' });
      console.error(error);
    } finally {
      setLoading(false);
    }
  }

  const fetchPendingApprovals = async () => {
    setLoading(true);
    try {
      const response = await apiClient.get('approvals/pending', {}, { action: 'fetch_pending_approvals' });
      if (response.status === 200) {
        const dataArray = Object.entries(response.data.data).map(([id, value]) => ({ id, ...value }));
        setApprovals(dataArray);
        setApprovalCache(response.data.data);
      }
    } catch (error) {
      console.error(error);
      if (shouldRedirectToLogin(error)) {
        setRedirect({ trigger: true, route: '/login' });
      }
    } finally {
      setLoading(false);
    }
  }


  // Get the approvals when the component is rendered
  React.useEffect(() => {
    setNavbarState({...navbarState, showBack: true, showOptions: false, showSearch: false})
    fetchPendingApprovals()
  }, [])

  const handleSearchChange = (event) => {
    setSearchValue(event.target.value);
  };

  const container = window !== undefined ? () => window().document.body : undefined;
  
  const cardSx = {
    borderRadius: '10px',
    boxShadow: 0,
    border: '1px solid',
    borderColor: 'divider',
  };

  return (
    <Container maxWidth="sm" disableGutters>
      {redirect.trigger && (
        <Navigate to={redirect.route} replace={true} />
      )}
      <Snackbar open={snackbar.open} autoHideDuration={5000} anchorOrigin={{ vertical: "top", horizontal: "center" }} sx={{mt: "3.2rem"}} onClose={handleSnackbarClose}>
        <Alert onClose={handleSnackbarClose} severity={snackbar.severity}>
          {snackbar.message}
        </Alert>
      </Snackbar>
      <Backdrop sx={{ color: '#fff', zIndex: (theme) => theme.zIndex.drawer + 1 }} open={loading}>
        <CircularProgress color="inherit" />
      </Backdrop>
      <Box sx={{ minHeight: '100vh', bgcolor: 'background.default', pb: 10 }}>
        <Box
          sx={{
            position: 'sticky',
            top: 0,
            zIndex: 10,
            px: 3,
            pt: 2,
            pb: 1.5,
            backgroundColor: (theme) => alpha(theme.palette.background.paper, 0.95),
            backdropFilter: 'blur(10px)',
          }}
        >
          <Box sx={{ mb: 2 }}>
            <PageHeader
              title="Aprobaciones"
              icon="clipboard-check"
              onBack={() => navigate(-1)}
            />
          </Box>
          <TextField 
            fullWidth 
            size="small" 
            disabled={approvals.length === 0}
            id='search' 
            label="Busqueda" 
            variant="outlined" 
            onChange={handleSearchChange}
            InputProps={{
              endAdornment: (
                <InputAdornment position="end">
                  <IconButton size="small">
                    <SearchIcon />
                  </IconButton>
                </InputAdornment>
              ),
              style: { borderRadius: '8px'}
            }}
          />
        </Box>

        <Box sx={{ px: 3, mt: 1.5 }}>
          {!loading && approvals.length === 0 && (
            <Card sx={{ ...cardSx, textAlign: 'center', py: 6 }}>
              <CardContent sx={{ p: 2, '&:last-child': { pb: 2 } }}>
                <PublishedWithChangesIcon sx={{ fontSize: 56, color: 'text.secondary', mb: 2 }} />
                <Typography sx={{ fontSize: 16, fontWeight: 800, color: 'text.primary', mb: 1 }}>No hay aprobaciones pendientes</Typography>
                <Typography sx={{ fontSize: 13, fontWeight: 600, color: 'text.secondary' }}>Revisa más tarde.</Typography>
              </CardContent>
            </Card>
          )}
          {!loading && filteredApprovals.length > 0 && (
            <Stack spacing={1.5}>
              {filteredApprovals.map((approval) => (
                <Card key={approval.id} sx={cardSx}>
                  <CardContent sx={{ p: 2, '&:last-child': { pb: 2 } }}>
                    <Typography sx={{ fontSize: 15, fontWeight: 800, color: 'text.primary', mb: 0.5 }}>
                      {approval.nombre}
                    </Typography>
                    <Typography sx={{ fontSize: 14, fontWeight: 700, color: 'text.primary' }}>
                      {approval.nombre_amenidad}
                    </Typography>
                    <Typography sx={{ fontSize: 13, fontWeight: 600, color: 'text.secondary', mb: 0.5 }}>
                      {convertEpochsToTimeString(approval.inicio, approval.fin)}
                    </Typography>
                    <Typography sx={{ fontSize: 12, fontWeight: 600, color: 'text.secondary' }}>
                      {approval.unit === "Administracion" || approval.unit === "Recepcion" ? "Unidad" : unitType}: {approval.unidad}
                    </Typography>
                    <Box sx={{ display: 'flex', justifyContent: 'flex-end', gap: 1.5, mt: 2 }}>
                      <Button size="small" variant="outlined" color="primary" onClick={() => removePre(approval.id)} sx={{ borderRadius: '10px', fontWeight: 700 }}>
                        Eliminar
                      </Button>
                      <Button size="small" variant='contained' color="secondary" onClick={() => approvePre(approval.id)} sx={{ borderRadius: '10px', fontWeight: 800, px: 4 }}>
                        Aprobar
                      </Button>
                    </Box>
                  </CardContent>
                </Card>
              ))}
            </Stack>
          )}
        </Box>
      </Box>
      {confirmDialog.open && (
        <Dialog open={confirmDialog.open} onClose={closeConfirmDialog}>
          <DialogTitle>{confirmDialog.approval.title}</DialogTitle>
          <DialogContent>
            <DialogContentText>
              <Typography>¿Está seguro de que desea {confirmDialog.approval.action} la reservación de <b>{confirmDialog.approval.nombre_amenidad} a nombre de {confirmDialog.approval.nombre}</b>?</Typography>
            </DialogContentText>
          </DialogContent>
          <DialogActions>
            <Button onClick={closeConfirmDialog} color="primary">
              Cancelar
            </Button>
            <Button onClick={() => runConfirmedAction(confirmDialog.approval.id,confirmDialog.approval.action)} color="secondary">
              Confirmar
            </Button>
          </DialogActions>
        </Dialog>
      )}
    </Container>
  );
}